import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchBuyerAtlStatus } from "../../Actions/BuyerAction";
import { section236HRateFor } from "../../global/Utils";

// What is known about the selected buyer's standing on the Active Taxpayer
// List. `source` says where `status` came from:
//   live     checked against the list just now
//   saved    the list could not be checked; the status saved on the buyer
//   none     the buyer has no NTN / CNIC, so cannot be on the list
//   unknown  the list could not be checked and the buyer has no saved status
const IDLE = { isChecking: false, status: "", source: "" };

/**
 * The invoice forms' Section 236H rate.
 *
 * `applySection236H(company, buyer)` is called whenever the invoice's company
 * or buyer changes. It sets the rate (through `setRate`) to the company's own
 * rate for the buyer's ATL status - the Active rate for a buyer who is Active
 * on the list, the Non-Active rate for anyone else:
 *
 *   1. straight away, from the status saved on the buyer, so the field is
 *      never left holding the previous buyer's rate;
 *   2. then again from the live list, which is the answer that counts - the
 *      list changes daily, and buyers added before the check existed have no
 *      saved status at all.
 *
 * An answer for a buyer / company that is no longer the one selected is
 * dropped. `buyerAtl` is for the note under the field.
 */
export const useSection236HRate = (setRate) => {
  const dispatch = useDispatch();
  const [buyerAtl, setBuyerAtl] = useState(IDLE);
  const latest = useRef(0);

  // Nothing lands on a form that has gone.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const applySection236H = useCallback(
    (company, buyer) => {
      const request = ++latest.current;
      if (!company || !buyer) {
        setBuyerAtl(IDLE);
        return;
      }
      const saved =
        buyer.atlStatus === "Active" || buyer.atlStatus === "Inactive"
          ? buyer.atlStatus
          : "";
      setRate(section236HRateFor(company, saved));

      const number = String(buyer.registrationNo || "").trim();
      if (!number) {
        setBuyerAtl({ isChecking: false, status: "Inactive", source: "none" });
        return;
      }
      setBuyerAtl({ isChecking: true, status: saved, source: "saved" });
      dispatch(fetchBuyerAtlStatus(number))
        .then((status) => {
          if (request !== latest.current) return;
          if (status !== "Active" && status !== "Inactive") {
            throw new Error("no ATL status");
          }
          setBuyerAtl({ isChecking: false, status, source: "live" });
          setRate(section236HRateFor(company, status));
        })
        .catch(() => {
          if (request !== latest.current) return;
          setBuyerAtl({
            isChecking: false,
            status: saved,
            source: saved ? "saved" : "unknown",
          });
        });
    },
    [dispatch, setRate],
  );

  return { buyerAtl, applySection236H };
};

/** The line under the GSection 236H % field: which rate was applied, and why. */
export const section236HNote = ({ isChecking, status, source }) => {
  if (isChecking) return "Checking the buyer's ATL status...";
  if (source === "live") {
    return status === "Active"
      ? "Buyer is Active on the ATL - the company's Active rate is applied."
      : "Buyer is not Active on the ATL - the company's Non-Active rate is applied.";
  }
  if (source === "none") {
    return "Buyer has no NTN / CNIC, so is not on the ATL - the company's Non-Active rate is applied.";
  }
  if (source === "saved") {
    return `The ATL could not be checked just now - the buyer's saved status (${status}) is used.`;
  }
  if (source === "unknown") {
    return "The buyer's ATL status could not be checked - the company's Non-Active rate is applied. Change the % if it is wrong.";
  }
  return "";
};
